import { CameraControls } from '@react-three/drei';
import { Canvas, useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Euler, Object3D, PCFSoftShadowMap } from 'three';
import { BVH } from 'three/examples/jsm/loaders/BVHLoader.js';
import { useSnapshot } from 'valtio';

import defaultVerticalOffset from '@cartwhl/3d/gltf/defaultVerticalOffsets';
import Character from '@cartwhl/3d/r3f/components/Character';
import Grid from '@cartwhl/3d/r3f/components/Grid';
import useFrameMixer from '@cartwhl/3d/r3f/hooks/useFrameMixer';
import {
  CharacterSettingsV1,
  FromStore,
  HAND_OPTIONS,
  RotationOffsets,
} from '@cartwhl/common/types';

import useLoadAnimation, {
  AnimationData,
  useTransformFilesToAnimationData,
} from 'hooks/useLoadAnimation';
import { AnimationPlayerStoreInstance } from 'stores/animationPlayerStoreFactory';

import usePlayerHotkeys from '../usePlayerHotkeys';
import CameraButtons from './CameraButtons';
import CanvasError from './CanvasError';
import MiniCameraControls from './MiniCameraControls';
import SceneSetup, {
  DEFAULT_CAMERA_POSITION,
  DEFAULT_THUMBNAIL_POSITION,
} from './SceneSetup';

export default function AnimationMiniPlayer({
  meshPath,
  rotationOffsets,
  characterID,
  playerName,
  animationPath,
  animationPlayerStore,
  bvhData,
  cameraFollow = false,
  defaultCameraPosition,
  forceShowSkeleton = false,
  frameRate = 24,
  frameStepSize = 1,
  characterSettings,
  playbackSpeed = 1,
  serializedCamera,
  showCameraButtons = false,
  showTightGrid = false,
  disableCameraControls,
  miniControls,
}: {
  meshPath?: string;
  characterID?: string;
  rotationOffsets?: RotationOffsets;
  playerName: string;
  animationPath?: string;
  animationPlayerStore: AnimationPlayerStoreInstance;
  bvhData?: FromStore<BVH>;
  cameraFollow?: boolean;
  defaultCameraPosition: 'close' | 'far';
  forceShowSkeleton?: boolean;
  frameRate?: number;
  frameStepSize?: number;
  characterSettings: FromStore<Omit<CharacterSettingsV1, 'version'>>;
  playbackSpeed?: number;
  serializedCamera?: string;
  showCameraButtons?: boolean;
  showTightGrid?: boolean;
  disableCameraControls?: boolean;
  miniControls?: React.ReactNode;
}) {
  const { trimFrameStart, trimFrameEnd, mixerReady } = useSnapshot(
    animationPlayerStore.state,
  );

  usePlayerHotkeys(playerName, animationPlayerStore);

  return (
    <>
      <div className="relative h-full w-full overflow-hidden rounded-[inherit]">
        <div className="dark:bg-gray-90 absolute top-0 right-0 bottom-0 left-0 z-0 bg-white">
          <div className="h-full w-full">
            <div className="bg-gray-30 dark:bg-gray-75 h-full w-full" />
          </div>
        </div>

        <div className="absolute top-3 left-3 z-10">
          {showCameraButtons && (
            <CameraButtons
              variant="simple"
              animationPlayerStore={animationPlayerStore}
              inModal
            />
          )}
        </div>

        {miniControls && mixerReady && (
          <div className="absolute right-3 bottom-3 left-3 z-10">
            {miniControls}
          </div>
        )}

        <MiniCanvas
          meshPath={meshPath}
          rotationOffsets={rotationOffsets}
          characterID={characterID}
          animationPath={animationPath}
          animationPlayerStore={animationPlayerStore}
          bvhData={bvhData}
          cameraFollow={cameraFollow}
          defaultCameraPosition={defaultCameraPosition}
          forceShowSkeleton={forceShowSkeleton}
          frameRate={frameRate}
          frameStepSize={frameStepSize}
          characterSettings={characterSettings}
          playbackSpeed={playbackSpeed}
          trimFrameStart={trimFrameStart}
          trimFrameEnd={trimFrameEnd}
          serializedCamera={serializedCamera}
          staticHands={
            (characterSettings?.staticHands as HAND_OPTIONS) ??
            HAND_OPTIONS.DEFAULT
          }
          showTightGrid={showTightGrid}
          disableCameraControls={disableCameraControls}
        />
      </div>
    </>
  );
}

export function MiniCanvas({
  meshPath,
  characterID,
  rotationOffsets,
  animationPath,
  animationPlayerStore,
  bvhData,
  cameraFollow,
  defaultCameraPosition,
  forceShowSkeleton,
  frameRate,
  frameStepSize,
  characterSettings,
  playbackSpeed,
  serializedCamera,
  staticHands,
  trimFrameStart,
  trimFrameEnd,
  showTightGrid,
  disableCameraControls,
}: {
  meshPath?: string;
  characterID?: string;
  rotationOffsets?: RotationOffsets;
  animationPath?: string;
  animationPlayerStore: AnimationPlayerStoreInstance;
  bvhData?: FromStore<BVH>;
  cameraFollow: boolean;
  defaultCameraPosition: 'close' | 'far';
  forceShowSkeleton: boolean;
  frameRate: number;
  frameStepSize: number;
  characterSettings?: FromStore<Omit<CharacterSettingsV1, 'version'>>;
  playbackSpeed?: number;
  serializedCamera?: string;
  staticHands: HAND_OPTIONS;
  trimFrameStart?: number;
  trimFrameEnd?: number;
  showTightGrid?: boolean;
  disableCameraControls?: boolean;
}) {
  const { enableRootMotion } = useSnapshot(animationPlayerStore.state);

  let animations: AnimationData[] | null = null;
  animations = useLoadAnimation(
    animationPath ? [animationPath] : [],
    undefined,
    {
      allowRootTranslation: true,
      staticHands,
      trimFrameStart,
      trimFrameEnd,
      enableRootMotion: enableRootMotion as [boolean, boolean, boolean],
      rotationOffsets: rotationOffsets as RotationOffsets,
      yOffset: defaultVerticalOffset(characterID ?? ''),
    },
  );
  const bvhFiles = useMemo(() => (bvhData ? [bvhData] : null), [bvhData]);
  const transformedAnimationData = useTransformFilesToAnimationData(bvhFiles, {
    allowRootTranslation: true,
    staticHands,
    trimFrameStart,
    trimFrameEnd,
    enableRootMotion: enableRootMotion as [boolean, boolean, boolean],
    rotationOffsets: rotationOffsets as RotationOffsets,
    yOffset: defaultVerticalOffset(characterID ?? ''),
  });
  if (transformedAnimationData) {
    animations = transformedAnimationData as AnimationData[];
  }

  const cameraControlsRef = useRef<CameraControls>(null);

  if (!characterSettings || !animations) return null;

  return (
    <ErrorBoundary
      fallback={<CanvasError />}
      onError={(error, errorInfo) => {
        console.error('Canvas error caught:', error, errorInfo);
      }}
    >
      <Canvas
        className={`absolute top-0 left-0 h-full w-full transition-opacity`}
        gl={{ preserveDrawingBuffer: true, alpha: false }}
        shadows={{
          enabled: true,
          autoUpdate: true,
          type: PCFSoftShadowMap,
        }}
      >
        <SceneSetup
          animationPlayerStore={animationPlayerStore}
          cameraControls={
            !disableCameraControls && (
              <MiniCameraControls
                animationPlayerStore={animationPlayerStore}
                cameraControlsRef={cameraControlsRef}
                defaultPosition={
                  defaultCameraPosition === 'close'
                    ? DEFAULT_THUMBNAIL_POSITION
                    : DEFAULT_CAMERA_POSITION
                }
              />
            )
          }
        />
        {showTightGrid && <Grid cellSize={0.25} />}
        <MiniStage
          animation={animations[0]}
          animationPlayerStore={animationPlayerStore}
          cameraFollow={cameraFollow}
          meshPath={meshPath}
          characterID={characterID}
          frameRate={frameRate}
          frameStepSize={frameStepSize}
          playbackSpeed={playbackSpeed}
          serializedCamera={serializedCamera}
          characterSettings={characterSettings}
          forceShowSkeleton={forceShowSkeleton}
        />
      </Canvas>
    </ErrorBoundary>
  );
}

function MiniStage({
  animation: { animationData, originalDuration },
  animationPlayerStore,
  cameraFollow,
  frameRate,
  frameStepSize,
  meshPath,
  characterID,
  playbackSpeed,
  serializedCamera,
  characterSettings,
  forceShowSkeleton,
}: {
  animation: AnimationData;
  animationPlayerStore: AnimationPlayerStoreInstance;
  cameraFollow: boolean;
  meshPath?: string;
  characterID?: string;
  frameRate?: number;
  frameStepSize?: number;
  playbackSpeed?: number;
  serializedCamera?: string;
  characterSettings?: FromStore<Omit<CharacterSettingsV1, 'version'>>;
  forceShowSkeleton?: boolean;
}) {
  const [characterScene, setCharacterScene] = useState<Object3D | null>(null);

  const mixer = useFrameMixer(animationData.clip, characterScene ?? undefined, {
    frameRate,
    frameStepSize,
    playbackSpeed,
    onReady: () => animationPlayerStore.setMixerReady(true),
    onPlayChange: animationPlayerStore.setIsPlaying,
    onFrameUpdate: (frame) => {
      animationPlayerStore.setCurrentFrame(frame);
    },
  });
  useEffect(() => {
    if (mixer) {
      animationPlayerStore.setDuration({
        ...mixer.duration,
        ...originalDuration,
      });
      // Hoisting the mixer to the global store
      animationPlayerStore.hoistAnimationMixer(mixer);
    }
  }, [animationPlayerStore, mixer, mixer.duration, originalDuration]);

  useFrame((state, delta) => {
    mixer.update(delta);

    animationPlayerStore.updateCamera({
      cameraFollow,
      delta,
      serializedCamera,
      target: state.scene.getObjectByName('pelvis'),
    });
  });

  if (!meshPath) return null;

  return (
    <Character
      characterID={characterID ?? ''}
      meshPath={meshPath}
      rotation={new Euler(-Math.PI / 2, 0, 0)}
      skinHex={characterSettings?.skinHex}
      faceExpression={characterSettings?.faceExpression}
      showSkeleton={characterSettings?.showSkeleton || forceShowSkeleton}
      onSceneReady={setCharacterScene}
    />
  );
}
